import axiosInstance from "@/axiosConfig";
import type { AccountingLinesResponse } from "@/shared/types/accountingLineDetail.types";
import type { JobServiceResponse } from "@/shared/types/job.types";
import { ACTIVITY_OPERATIONS_ENDPOINTS } from "../configs/endpoints";
import type {
  ActivityCatalogOption,
  ActivityOperationsBalance,
  ActivityOperationsFilters,
  ActivityOperationsResponse,
  ActivityReferenceOption,
  CreateAdjustmentPayload,
  InvoiceDetail,
  SubsidyRuleOption,
} from "../types/activityOperations.types";

const buildParams = (filters: ActivityOperationsFilters): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.page) params.set("page", String(filters.page));
  if (filters.per_page) params.set("per_page", String(filters.per_page));
  if (filters.search) params.set("search", filters.search);
  if (filters.categorie) params.set("categorie", filters.categorie);
  if (filters.genre_id) params.set("genre_id", filters.genre_id);
  if (filters.famille_id) params.set("famille_id", filters.famille_id);
  if (filters.reference_id) params.set("reference_id", filters.reference_id);
  if (filters.holder_type) params.set("holder_type", filters.holder_type);
  if (filters.holder_id) params.set("holder_id", filters.holder_id);
  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  if (filters.manual) params.set("manual", filters.manual);
  return params;
};

export class ActivityOperationsService {
  static async getOperations(
    filters: ActivityOperationsFilters = {},
    sortParams: string[] = [],
  ): Promise<ActivityOperationsResponse> {
    const params = buildParams(filters);
    sortParams.forEach((sort) => params.append("sort[]", sort));
    const url = `${ACTIVITY_OPERATIONS_ENDPOINTS.list}?${params.toString()}`;
    const { data } = await axiosInstance.get<ActivityOperationsResponse>(url);
    return data;
  }

  static async getBalance(filters: ActivityOperationsFilters = {}): Promise<ActivityOperationsBalance> {
    const params = buildParams(filters);
    params.delete("page");
    params.delete("per_page");
    const url = `${ACTIVITY_OPERATIONS_ENDPOINTS.balance}?${params.toString()}`;
    const { data } = await axiosInstance.get<ActivityOperationsBalance>(url);
    return data;
  }

  static async getModules(): Promise<string[]> {
    const { data } = await axiosInstance.get<{ modules: string[] }>(ACTIVITY_OPERATIONS_ENDPOINTS.modules);
    return data.modules;
  }

  static async getReferences(categorie?: string, familleId?: string): Promise<ActivityReferenceOption[]> {
    const params = new URLSearchParams();
    if (categorie) params.set("categorie", categorie);
    if (familleId) params.set("famille_id", familleId);
    const url = `${ACTIVITY_OPERATIONS_ENDPOINTS.references}?${params.toString()}`;
    const { data } = await axiosInstance.get<{ references: ActivityReferenceOption[] }>(url);
    return data.references;
  }

  static async getGenres(categorie?: string): Promise<ActivityCatalogOption[]> {
    const params = new URLSearchParams();
    if (categorie) params.set("categorie", categorie);
    const url = `${ACTIVITY_OPERATIONS_ENDPOINTS.genres}?${params.toString()}`;
    const { data } = await axiosInstance.get<{ genres: ActivityCatalogOption[] }>(url);
    return data.genres;
  }

  static async getFamilles(categorie?: string, genreId?: string): Promise<ActivityCatalogOption[]> {
    const params = new URLSearchParams();
    if (categorie) params.set("categorie", categorie);
    if (genreId) params.set("genre_id", genreId);
    const url = `${ACTIVITY_OPERATIONS_ENDPOINTS.familles}?${params.toString()}`;
    const { data } = await axiosInstance.get<{ familles: ActivityCatalogOption[] }>(url);
    return data.familles;
  }

  static async exportXlsx(filters: ActivityOperationsFilters = {}): Promise<JobServiceResponse> {
    const body: Record<string, unknown> = {};
    if (filters.search) body.search = filters.search;
    if (filters.categorie) body.categorie = filters.categorie;
    if (filters.genre_id) body.genre_id = filters.genre_id;
    if (filters.famille_id) body.famille_id = filters.famille_id;
    if (filters.reference_id) body.reference_id = filters.reference_id;
    if (filters.holder_type) body.holder_type = filters.holder_type;
    if (filters.holder_id) body.holder_id = filters.holder_id;
    if (filters.date_from) body.date_from = filters.date_from;
    if (filters.date_to) body.date_to = filters.date_to;
    if (filters.manual) body.manual = filters.manual;

    const { data } = await axiosInstance.post<JobServiceResponse>(ACTIVITY_OPERATIONS_ENDPOINTS.exportXlsx, body);
    return data;
  }

  static async createAdjustment(payload: CreateAdjustmentPayload) {
    const { data } = await axiosInstance.post(ACTIVITY_OPERATIONS_ENDPOINTS.createAdjustment, payload);
    return data;
  }

  static async getPolicyRules(policyId: string): Promise<SubsidyRuleOption[]> {
    const { data } = await axiosInstance.get<{ rules: SubsidyRuleOption[] }>(
      ACTIVITY_OPERATIONS_ENDPOINTS.policyRules(policyId),
    );
    return data.rules ?? [];
  }

  static async getInvoice(id: string): Promise<InvoiceDetail> {
    const { data } = await axiosInstance.get<InvoiceDetail>(ACTIVITY_OPERATIONS_ENDPOINTS.invoice(id));
    return data;
  }

  // ---------------------------------------------------------------------------
  // Onglet Comptabilité (iso bookings / ecom-orders / refunds) : règlement et
  // compensation employé-centrés des AO depuis la fiche facture/avoir.
  // ---------------------------------------------------------------------------

  /** Toutes les lignes comptables du salarié porteur de la facture/avoir. */
  static async getAccountingLines(
    invoiceId: string,
    includeSettled = false,
  ): Promise<AccountingLinesResponse> {
    const { data } = await axiosInstance.get<AccountingLinesResponse>(
      ACTIVITY_OPERATIONS_ENDPOINTS.invoiceAccountingLines(invoiceId),
      { params: { include_settled: includeSettled } },
    );
    return data;
  }

  /** Compensation cross-AO (`amounts` map ao_id → montant signé, somme = 0). */
  static async compensateOperations(
    invoiceId: string,
    operationIds: string[],
    amounts?: Record<string, number>,
  ): Promise<AccountingLinesResponse> {
    const { data } = await axiosInstance.post<AccountingLinesResponse>(
      ACTIVITY_OPERATIONS_ENDPOINTS.invoiceCompensate(invoiceId),
      { operation_ids: operationIds, amounts },
    );
    return data;
  }

  /** Affecte les AO choisies à un mouvement bancaire OU un règlement existant. */
  static async settleWithPayment(
    invoiceId: string,
    payload: {
      operation_ids: string[]
      settlement_id?: string | null
      movement_id?: string | null
    },
  ): Promise<AccountingLinesResponse> {
    const { data } = await axiosInstance.post<AccountingLinesResponse>(
      ACTIVITY_OPERATIONS_ENDPOINTS.invoiceSettleWithPayment(invoiceId),
      payload,
    );
    return data;
  }
}
